import { readAuthSession } from "../services/authSession";
import {
  readCanvasSession,
  readStoredTicket,
} from "../services/canvasSession";
import { ticketNameParts, ticketUserId } from "../utils/ticket";
import {
  resolvePaymentOrderType,
  resolvePaymentRedirectUrl,
} from "./mosidaApi";
import { calculateMuseumFeeTaxTotals } from "./museumFeeTax";
import { createPayOnReceptionOrder } from "./orderService";
import {
  createTempData,
  resolvePaymentUserId,
  scanQRPaymentAPI,
} from "./paymentService";
import {
  persistPaymentSessionFromTempData,
  savePendingPaymentOrderFromApiOrders,
} from "./pendingPaymentOrder";
import { buildPaymentTempPayload, cartTotal } from "./shopUtils";

function buildCheckoutUserInfo(guestEmail) {
  const stored = readCanvasSession() || {};
  const ticket = readStoredTicket() || stored.ticket || null;
  const names = ticketNameParts(ticket);
  const auth = readAuthSession();
  const user = auth?.user || null;
  const userId =
    ticketUserId(ticket) || user?.id || stored.userId || stored.user_id || null;

  const userInfo = {};
  if (names.firstName) userInfo.firstName = names.firstName;
  if (names.lastName) userInfo.lastName = names.lastName;
  if (names.fullName) userInfo.fullName = names.fullName;
  if (ticket?.id || ticket?.ticketId || stored.ticketId) {
    userInfo.ticketId = ticket?.id || ticket?.ticketId || stored.ticketId;
  }
  if (userId) userInfo.userId = userId;
  if (user?.email) userInfo.email = user.email;
  if (user?.name || user?.fullName) {
    userInfo.fullName = user.name || user.fullName || userInfo.fullName;
  }
  if (guestEmail) userInfo.email = guestEmail;
  return userInfo;
}

export function cartFeeBreakdown(cartItems, museumFees) {
  const subtotal = cartTotal(cartItems);
  return calculateMuseumFeeTaxTotals(subtotal, museumFees || undefined);
}

/**
 * Build Giftshop-aligned temp-data checkout payload (fees + optional guest email).
 */
export function buildCheckoutPayload(cartItems, options = {}) {
  const {
    artUrl,
    museumFees,
    guestEmail,
    paymentMethod,
    status,
  } = options;
  const pricing = cartFeeBreakdown(cartItems, museumFees);
  const userInfo = buildCheckoutUserInfo(guestEmail);
  const loggedInUserId = resolvePaymentUserId(userInfo.userId || null);

  const base = buildPaymentTempPayload(cartItems, {
    artUrl,
    currency: "USD",
    pricing,
    userInfo,
    userId: loggedInUserId,
  });

  return {
    ...base,
    totalAmount: pricing.total,
    subtotal: pricing.subtotal,
    processingFee: pricing.processingFee,
    salesTax: pricing.salesTax,
    processingFeePercent: pricing.processingFeePercent,
    salesTaxPercent: pricing.salesTaxPercent,
    ...(paymentMethod ? { paymentMethod } : {}),
    ...(status ? { status } : {}),
    ...(loggedInUserId ? { userId: loggedInUserId, user_id: loggedInUserId } : {}),
    userInfo: Object.keys(userInfo).length > 0 ? userInfo : undefined,
  };
}

function extractQrUrl(data) {
  if (typeof data === "string") return data;
  if (data && typeof data === "object") {
    return data.qrCodeUrl || data.qrCode || data.paymentLink || "";
  }
  return "";
}

/** Pay Now → temp-data + payment link. Returns { qrCodeUrl, tempDataId, totalAmount }. */
export async function startQrCheckout(cartItems, options = {}) {
  if (!cartItems?.length) {
    throw new Error("Your cart is empty.");
  }
  const payload = buildCheckoutPayload(cartItems, options);
  const tempResponse = await createTempData({ data: payload });
  const tempDataId = tempResponse.data?.id;
  if (!tempResponse.success || !tempDataId) {
    throw new Error(tempResponse.error || "Failed to create temp data");
  }
  persistPaymentSessionFromTempData(tempResponse.data);

  const orderType = resolvePaymentOrderType(payload.items, "onsite");
  const loggedInUserId = resolvePaymentUserId(payload.userId || null);
  const url = resolvePaymentRedirectUrl(payload.items, {
    paymentType: "onsite",
  });
  const response = await scanQRPaymentAPI(tempDataId, {
    url,
    type: orderType,
    ...(loggedInUserId
      ? { data: { userId: loggedInUserId, user_id: loggedInUserId } }
      : {}),
  });
  if (!response.success || !response.data) {
    throw new Error(response.error || "Failed to generate payment QR code");
  }
  const qrCodeUrl = String(extractQrUrl(response.data) || "").trim();
  const isUsableQr =
    /^https?:\/\//i.test(qrCodeUrl) ||
    /^data:image\//i.test(qrCodeUrl) ||
    qrCodeUrl.startsWith("blob:");
  if (!qrCodeUrl || !isUsableQr) {
    throw new Error("QR code URL not found in response");
  }
  return {
    qrCodeUrl,
    tempDataId,
    totalAmount: payload.totalAmount,
    orderType,
  };
}

/** Pay at counter → temp-data + pay-on-reception orders. Returns session with order numbers. */
export async function startPayOnReceptionCheckout(cartItems, guestEmail, options = {}) {
  if (!cartItems?.length) {
    throw new Error("Your cart is empty.");
  }
  if (!guestEmail) {
    throw new Error("Email is required.");
  }
  const payload = buildCheckoutPayload(cartItems, {
    ...options,
    guestEmail,
    paymentMethod: "pay_on_reception",
    status: "payment-pending",
  });
  const tempResponse = await createTempData({ data: payload });
  const tempDataId = tempResponse.data?.id;
  if (!tempResponse.success || !tempDataId) {
    throw new Error(tempResponse.error || "Failed to create temp data");
  }
  persistPaymentSessionFromTempData(tempResponse.data);

  const orderType = resolvePaymentOrderType(payload.items, "onsite");
  const orderResponse = await createPayOnReceptionOrder(
    {
      temp_data_id: tempDataId,
      email: guestEmail || payload.userInfo?.email,
    },
    orderType
  );
  if (!orderResponse.success || !orderResponse.data?.length) {
    throw new Error(
      orderResponse.error || "Failed to place pay-on-reception order"
    );
  }
  const session = savePendingPaymentOrderFromApiOrders(
    orderResponse.data,
    tempDataId
  );
  const orderNumbers =
    session.orderNumbers ||
    (session.orderNumber ? [session.orderNumber] : []);
  return { session, orderNumbers, tempDataId, orderType };
}
